import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector } from '@angular/core';
import { Media } from '../../models/media';
import { MediaViewerOverlayRef } from './media-viewer-overlay-ref';
import { MEDIA_VIEWER_CONFIG, MediaViewerConfig, MediaViewerOverlayComponent } from './media-viewer-overlay.component';
import { take } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MediaViewerOverlayService {

  protected static overlayRefs: Map<string, MediaViewerOverlayRef> = new Map();

  constructor(private injector: Injector, private overlay: Overlay) {}

  public open(config: MediaViewerConfig): Promise<MediaViewerOverlayRef> {
    const media = config?.media;
    const isConferenceMedia = media?.type == 'conference';
    return new Promise((resolve) => {
      if (isConferenceMedia) {
        const promises: Promise<void>[] = [];
        //remove all open conferences - only one active conference is allowed
        MediaViewerOverlayService.overlayRefs.forEach((overlayRef, mediaId) => {
          const viewerMedia = overlayRef.config.media; 
          if (viewerMedia?.id!=media.id && viewerMedia?.type == 'conference') {
            promises.push(new Promise(resolve => {
              overlayRef.onDestroy.pipe(take(1)).subscribe(callback => {
                callback?.();
                resolve();
              })
            }));
            overlayRef.destroy();
          }
        });
        Promise.all(promises).then(resolve);
      } else {
        resolve(void 0);
      }
    }).then(() => {
      const overlayRef = this.createOverlay(isConferenceMedia);
      const mediaViewerOverlayRef = new MediaViewerOverlayRef(overlayRef, config);
      const overlayComponent = this.attachOverlayContainer(overlayRef, config, mediaViewerOverlayRef);
      overlayComponent.viewer.media = media;
      MediaViewerOverlayService.overlayRefs.set(media.id, mediaViewerOverlayRef);
      return mediaViewerOverlayRef;
    });
  }

  private createOverlay(disposeOnNavigation: boolean): OverlayRef {
    const overlayConfig = this.createOverlayConfig(disposeOnNavigation);
    return this.overlay.create(overlayConfig);
  }

  private attachOverlayContainer(
    overlayRef: OverlayRef,
    context: MediaViewerConfig,
    mediaViewerOverlayRef: MediaViewerOverlayRef
  ): MediaViewerOverlayComponent {
    const injector = this.createInjector(context, mediaViewerOverlayRef);
    const containerPortal = new ComponentPortal(MediaViewerOverlayComponent, null, injector);
    const containerRef: ComponentRef<MediaViewerOverlayComponent> = overlayRef.attach(containerPortal);
    return containerRef.instance;
  }

  private createInjector(context: MediaViewerConfig, mediaViewerOverlayRef: MediaViewerOverlayRef): Injector {
    return Injector.create({
      parent: this.injector,
      providers: [
        { provide: MediaViewerOverlayRef, useValue: mediaViewerOverlayRef },
        { provide: MEDIA_VIEWER_CONFIG, useValue: context }
      ]
    });
  }

  private createOverlayConfig(disposeOnNavigation = true): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();
    const overlayConfig = new OverlayConfig({
      panelClass: ['overlay-media-viewer'],
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy,
      disposeOnNavigation
    });
    return overlayConfig;
  }
}